import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios'; // ✅ Import axios for better handling
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const Flexibility = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'flexibility'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]); // ✅ State to hold videos
  const [loading, setLoading] = useState(true); // ✅ Loading state

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="page-container flexibility-page">
            <p class="disclaimer">
            Disclaimer: As an Amazon Associate, I earn from qualifying purchases. If you make a purchase through the links here, 
            I may earn a small commission at no extra cost to you. Thank you for your support.
            </p>
    <div className="micro-void"></div>
      <h1>Flexibility and Mobility</h1>

      <img src="/images/wf-flexibility.webp" alt="Flexibility and Mobility" className="page-image" />

      <p className="page-intro">
        Flexibility and mobility are crucial components of overall physical health that allow the
        body to move efficiently and safely. Flexibility refers to the ability of your muscles and
        joints to stretch and move through their full range of motion, while mobility includes the
        movement of your joints with control and stability.
      </p>

      <p>
        Regular flexibility and mobility training can help improve posture, reduce the risk of
        injury, alleviate tension, and enhance athletic performance. This practice is beneficial for
        everyone, whether you're an athlete looking to improve performance or someone simply seeking
        to feel more comfortable in everyday movements.
      </p>

      <h2>Health Benefits of Flexibility and Mobility Training</h2>
      <ul className="content-list">
        <li>Increased range of motion and joint mobility</li>
        <li>Improved posture and alignment</li>
        <li>Reduced muscle stiffness and tension</li>
        <li>Enhanced athletic performance and endurance</li>
        <li>Reduced risk of injury during exercise and daily activities</li>
      </ul>

      <hr className="section-divider" />
       {/* Additional Content: Flexibility Habits */}
       <h2 className="section-title">Create a Flexibility and Mobility Routine</h2>
      <p className="section-description">
        Regular flexibility and mobility exercises are essential for maintaining healthy joints, muscles, and an active lifestyle.
        Here’s a simple framework to help you integrate flexibility into your daily routine:
      </p>

      <h3 className="content-subtitle">Framework for Building a Flexibility Habit:</h3>
      <ul className="content-list">
        <li>
          <strong>Start Small:</strong> Begin with a short 5-10 minute session of stretching or mobility exercises.
        </li>
        <li>
          <strong>Make It Regular:</strong> Commit to at least 3-4 days a week for consistent improvement.
        </li>
        <li>
          <strong>Track Your Progress:</strong> Use a fitness app or journal to track your progress.
        </li>
      </ul>

      <h3 className="content-subtitle">Examples of Flexibility and Mobility Exercises:</h3>
      <ul className="content-list">
        <li>Gentle neck and shoulder stretches.</li>
        <li>Hip-opening stretches such as lunges.</li>
        <li>Dynamic warm-up stretches (leg swings, arm circles).</li>
        <li>Foam rolling to relieve muscle tightness.</li>
      </ul>

      <p className="section-description">
        Incorporating just a few minutes of stretching and mobility exercises into your day can make a big difference in your physical health.
      </p>

      <hr className="section-divider" />
      {/* Curated Video Collection */}
      <h2 className="section-title">Curated Video Collection</h2>

      {loading ? (
        <p className="loading-message">Loading videos...</p>
      ) : (
        videos.length > 0 ? (
          videos.map((video) => (
            <div key={video._id} className="video-card">
              <h3 className="video-title">{video.title}</h3>
              <p className="video-details">
                <strong>Source:</strong> {video.source} <br />
                <strong>Length:</strong> {video.length} <br />
                {video.description}
              </p>
              <iframe
                className="video-frame"
                src={video.url}
                title={video.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>

              {/* ✅ Helpful Button */}
              <div className="video-actions">
                <button
                  onClick={() => handleMarkHelpful(video._id)}
                  disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  className={`button ${video.usersWhoClickedHelpful?.includes(user?.id) ? "disabled" : ""}`}
                >
                  {video.usersWhoClickedHelpful?.includes(user?.id)
                    ? `Helpful (${video.helpfulCount})`
                    : `Mark as Helpful (${video.helpfulCount || 0})`}
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="no-videos-message">No videos found for this category.</p>
        )
      )}

        <hr className="section-divider" />
        <h2 className="section-title">Curated Book Collection</h2>

        {/* Book 1: Stretching: 30th Anniversary Edition */}
        <div className="book-card">
          <h3 className="book-title">Stretching: 40th Anniversary Edition</h3>

          {/* Book Cover Image from Open Library */}
          <img 
            src="https://covers.openlibrary.org/b/id/714468-M.jpg" // Open Library cover image URL
            alt="Stretching: 40th Anniversary Edition by Bob Anderson" 
            className="book-cover" 
          />
          
          {/* Attribution for Image Source */}
          <p className="image-source">Image Source: Open Library API</p>

          <div className="micro-void"></div>

          <p className="book-details">
            <strong>Author:</strong> Bob Anderson <br />
            <div className="micro-void"></div>
            <strong>Overview:</strong> Stretching: 40th Anniversary Edition is a classic stretching guide that has been a go-to resource for improving flexibility for decades. The 40th edition features updated content and exercises for people of all ages and flexibility levels. It covers stretches for the whole body, as well as warm-up and cool-down routines. <br />
            <div className="barely-void"></div>
            <strong>Why It's Great:</strong> This book is ideal for beginners and experienced fitness enthusiasts alike. It provides clear instructions for improving flexibility and reducing muscle stiffness, making it a valuable resource for daily stretching routines.
          </p>

          <div className="micro-void"></div>

          <a
            href="https://amzn.to/4imrKuS" // Your actual Amazon affiliate link
            className="amazon-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Buy on Amazon-https://amzn.to/4imrKuS
          </a>

          {/* Affiliate Link Disclosure */}
          <p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>
          <div className="short-line"></div>
        </div>

        {/* Book 2: Becoming a Supple Leopard */}
        <div className="book-card">
          <h3 className="book-title">Becoming a Supple Leopard</h3>

          {/* Book Cover Image from Open Library */}
          <img 
            src="https://covers.openlibrary.org/b/id/8745350-M.jpg" // Open Library cover image URL
            alt="Becoming a Supple Leopard by Kelly Starrett" 
            className="book-cover" 
          />
          
          {/* Attribution for Image Source */}
          <p className="image-source">Image Source: Open Library API</p>

          <div className="micro-void"></div>

          <p className="book-details">
            <strong>Author:</strong> Kelly Starrett <br />
            <div className="micro-void"></div>
            <strong>Overview:</strong> *Becoming a Supple Leopard* focuses on improving mobility and flexibility through proper movement patterns. Kelly Starrett uses techniques from his mobility coaching to help individuals reduce pain, prevent injury, and increase their performance in athletic activities. <br />
            <div className="barely-void"></div>
            <strong>Why It's Great:</strong> Starrett’s approach is comprehensive, addressing not just flexibility but overall mobility, which is essential for injury prevention and optimal athletic performance. This book is perfect for athletes looking to improve their range of motion and functional movement.
          </p>

          <div className="micro-void"></div>

          <a
            href="https://amzn.to/4ioaqpm" // Your actual Amazon affiliate link
            className="amazon-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Buy on Amazon-https://amzn.to/4ioaqpm
          </a>

          {/* Affiliate Link Disclosure */}
          <p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>
          <div className="short-line"></div>
        </div>

        {/* Book 3: The Stretching Handbook */}
        <div className="book-card">
        <h3 className="book-title">The Anatomy of Stretching</h3>

        {/* Book Cover Image from Open Library */}
        <img 
          src="https://covers.openlibrary.org/b/id/7791733-L.jpg" // Open Library cover image URL
          alt="The Anatomy of Stretching by Brad Walker" 
          className="book-cover" 
          style={{ maxWidth: "170px", height: "auto" }} 
        />

        {/* Attribution for Image Source */}
        <p className="image-source">Image Source: Open Library API</p>

        <div className="micro-void"></div>

        <p className="book-details">
          <strong>Author:</strong> Brad Walker <br />
          <div className="micro-void"></div>
          <strong>Overview:</strong> The Anatomy of Stretching is a comprehensive guide that combines anatomical knowledge with practical stretching techniques. It explains the muscles involved in each stretch, with detailed illustrations and explanations of how to stretch safely and effectively. <br />
          <div className="barely-void"></div>
          <strong>Why It's Great:</strong> This book provides an in-depth understanding of how stretching affects different muscle groups, making it an essential resource for those looking to improve flexibility, prevent injuries, and enhance their performance in any sport or fitness activity.
        </p>

        <div className="micro-void"></div>

        <a
          href="https://amzn.to/41GdUw5" // Your actual Amazon affiliate link
          className="amazon-link"
          target="_blank"
          rel="noopener noreferrer"
          
        >
          Buy on Amazon-https://amzn.to/41GdUw5
        </a>

        {/* Affiliate Link Disclosure */}
        <p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>
        <div className="short-line"></div>
        </div>

        <hr className="section-divider" />


     
     

      {/* Apps Section */}
      <h2 className="section-title">Popular Flexibility and Mobility Apps</h2>
      <p className="section-description">
        To enhance your flexibility and mobility training, try out these apps:
      </p>

      <h3 className="content-subtitle">Stretching Apps</h3>
      <p className="section-description">
        Apps like <strong>StretchIt</strong> and <strong>ROMWOD</strong> offer guided flexibility exercises.
      </p>

      <h3 className="content-subtitle">Yoga Apps</h3>
      <p className="section-description">
        <strong>Daily Yoga</strong> and <strong>Yoga Studio</strong> improve flexibility and reduce stress.
      </p>

      <hr />

      {/* Forum Section */}
      <div className="forum-section">
        <h2>Join the Discussion</h2>
        <p>
          Have questions or insights about flexibility and mobility? Join our forum to connect with
          others who are exploring this topic.
        </p>
        <a href="/forum/Flexibility" className="forum-link">
          Go to Flexibility Forum
        </a>
      </div>
    </div>
  );
};

export default Flexibility;
